import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    TrendingUp, ListChecks, History, RefreshCw, CheckCircle, XCircle, Clock,
    AlertTriangle, ShieldCheck, Hourglass, Ban, ClipboardPenLine, ChevronLeft, ChevronRight,
    ChevronsUpDown, ChevronUp, ChevronDown, Info, ClipboardCheck, LayoutTemplate,
} from 'lucide-react';
import RHDesempenhoAvaliacao from '../desempenho/RHDesempenhoAvaliacao';
import RHDesempenhoVersoes from '../desempenho/RHDesempenhoVersoes';

/**
 * Submódulo RH — Transição de Cargo.
 * Três abas: Elegíveis (monitoramento calculado), Lançar Avaliação (3 notas +
 * prévia do resultado) e Histórico (avaliações + efetivação).
 * Regra/spec: docs/PLANO.md › "Submódulo RH — Transição de Cargo".
 * O backend é a autoridade do cálculo; a prévia aqui é só visual.
 */
const API_BASE = '/api/rh/transicao-cargo';
const ITENS_POR_PAGINA = 50;

// Ordem dos níveis na escada — usada para ordenar os chips de filtro.
// (Coincide com a ordem alfabética: JR<PL<SR e I<II<III, então a ordenação
// da coluna "Nível" por string também sai na ordem certa.)
const ORDEM_NIVEIS = ['JR', 'PL', 'PL I', 'PL II', 'PL III', 'SR', 'SR I', 'SR II', 'SR III'];

// Ordem dos cards de status na aba Elegíveis.
const STATUS_ORDEM = ['ELEGIVEL', 'AGUARDANDO_REAVALIACAO', 'NAO_ELEGIVEL_TEMPO', 'APROVADO_AGUARDANDO_EFETIVACAO', 'FORA_PPI', 'INDEFINIDO'];

// Colunas numéricas do Histórico (ordenam por valor, não por texto).
const NUMERICAS_HIST = new Set(['NOTA_LIVE', 'NOTA_TEORICA', 'NOTA_DESEMPENHO', 'NOTA_OPERACIONAL', 'NOTA_FINAL']);
// Coluna numérica dos Elegíveis (qtd de reprovações ordena por valor).
const NUMERICAS_ELEG = new Set(['QTD_REPROVACOES']);

const formatarData = (data) => {
    if (!data) return '-';
    const [y, m, d] = String(data).split('T')[0].split('-');
    if (!y || !m || !d) return '-';
    return `${d}/${m}/${y}`;
};

// Hoje em 'YYYY-MM-DD' no fuso LOCAL (não `toISOString()`, que é UTC e viraria o
// dia depois das 21h em Brasília). É o padrão do campo "Data de aplicação" e o
// teto do seletor — não se aplica uma avaliação no futuro.
const hojeISO = () => {
    const d = new Date();
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

// 'YYYY-MM-DD' → nº de dias de hoje até a data (positivo = futuro, negativo = passado).
const diasAte = (s) => {
    if (!s) return null;
    const [y, m, d] = String(s).split('T')[0].split('-').map(Number);
    if (!y || !m || !d) return null;
    const alvo = new Date(y, m - 1, d);
    const hoje = new Date(); hoje.setHours(0, 0, 0, 0);
    return Math.round((alvo - hoje) / 86400000);
};

const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

// Prévia local do cálculo — espelha a fórmula do backend (que é a autoridade).
const calcularPrevia = (live, teorica, desempenho) => {
    const L = Number(live), T = Number(teorica), D = Number(desempenho);
    if ([L, T, D].some((n) => !Number.isFinite(n))) return null;
    const operacional = round2((L + T) / 2);
    const final = round2(L * 0.35 + T * 0.35 + D * 0.3);
    return { operacional, final, resultado: final >= 90 ? 'APROVADO' : 'REPROVADO' };
};

// Ordenação genérica (mesmo padrão da Consulta dos Primeiros 15): se `numericas`
// inclui a chave, compara por número; senão compara como texto. sortConfig=null
// mantém a ordem original (já filtrada).
const aplicarSort = (arr, sortConfig, numericas) => {
    if (!sortConfig) return arr;
    const { key, dir } = sortConfig;
    const mult = dir === 'asc' ? 1 : -1;
    return [...arr].sort((a, b) => {
        if (numericas && numericas.has(key)) return ((Number(a[key]) || 0) - (Number(b[key]) || 0)) * mult;
        const va = (a[key] ?? '').toString().toLowerCase();
        const vb = (b[key] ?? '').toString().toLowerCase();
        return va < vb ? -mult : va > vb ? mult : 0;
    });
};
// Alterna asc/desc ao clicar de novo na mesma coluna; nova coluna começa em asc.
const toggleSort = (setSort) => (key) => setSort((prev) =>
    prev && prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });

// Cor/ícone por status de elegibilidade.
const ESTILO_STATUS = {
    ELEGIVEL:                      { color: '#065f46', bg: '#d1fae5', icon: CheckCircle },
    NAO_ELEGIVEL_TEMPO:            { color: '#92400e', bg: '#fef3c7', icon: Clock },
    AGUARDANDO_REAVALIACAO:        { color: '#9a3412', bg: '#ffedd5', icon: Hourglass },
    APROVADO_AGUARDANDO_EFETIVACAO:{ color: '#1d4ed8', bg: '#dbeafe', icon: ShieldCheck },
    FORA_PPI:                      { color: '#475569', bg: '#f1f5f9', icon: Ban },
    INDEFINIDO:                    { color: '#b91c1c', bg: '#fee2e2', icon: AlertTriangle },
};

const ROTULO_STATUS = {
    ELEGIVEL: 'Elegível', NAO_ELEGIVEL_TEMPO: 'Não elegível (tempo)', AGUARDANDO_REAVALIACAO: 'Aguardando reavaliação',
    APROVADO_AGUARDANDO_EFETIVACAO: 'Aprovado — aguardando efetivação', FORA_PPI: 'Fora da transição (PPI)', INDEFINIDO: 'Indefinido',
};

const RHTransicaoCargo = () => {
    const token = localStorage.getItem('authToken');
    const headers = useMemo(() => ({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }), [token]);

    const [abaAtiva, setAbaAtiva] = useState('elegiveis');
    const [elegiveis, setElegiveis] = useState([]);
    const [historico, setHistorico] = useState([]);
    const [loading, setLoading] = useState(false);
    const [erro, setErro] = useState(null);

    // Filtros + ordenação aba Elegíveis
    const [filtroCargo, setFiltroCargo] = useState('');
    const [filtroLoja, setFiltroLoja] = useState('');
    // Abre já filtrado em "Elegível" — a situação acionável do dia (quem tem prova
    // para lançar). As demais situações seguem a um clique no card/seletor.
    const [filtroStatus, setFiltroStatus] = useState('ELEGIVEL');
    const [filtroNiveis, setFiltroNiveis] = useState([]); // chips multi-seleção
    const [sortElegiveis, setSortElegiveis] = useState(null);
    const [pagina, setPagina] = useState(1);

    // Aba Lançar
    const [selecionadoId, setSelecionadoId] = useState('');
    const [notas, setNotas] = useState({ live: '', teorica: '', desempenho: '' });
    // Data em que a avaliação foi aplicada — hoje por padrão; uma data anterior
    // registra avaliações já realizadas (pedido do Treinamento, 2026-09-23).
    const [dataAplicacao, setDataAplicacao] = useState(() => hojeISO());
    const [observacao, setObservacao] = useState('');
    const [salvando, setSalvando] = useState(false);
    const [feedback, setFeedback] = useState(null); // { ok, msg }
    // Nota de Desempenho pré-preenchida da última avaliação de desempenho FINALIZADA
    // do funcionário (módulo Desempenho) — o operador ainda pode ajustar.
    const [desempenhoAuto, setDesempenhoAuto] = useState(null); // { notaFinal } | null

    // Filtros + ordenação aba Histórico
    const [filtroCargoHist, setFiltroCargoHist] = useState('');
    const [filtroResultadoHist, setFiltroResultadoHist] = useState('');
    const [sortHistorico, setSortHistorico] = useState(null);
    const [paginaHist, setPaginaHist] = useState(1);

    // ─── Fetchers ────────────────────────────────────────────────────────────
    const buscarElegiveis = useCallback(async () => {
        setLoading(true); setErro(null);
        try {
            const res = await fetch(`${API_BASE}/elegiveis`, { headers });
            if (!res.ok) throw new Error('Erro ao buscar elegíveis.');
            setElegiveis(await res.json());
            setPagina(1);
        } catch (e) { setErro(e.message); } finally { setLoading(false); }
    }, [headers]);

    const buscarHistorico = useCallback(async () => {
        setLoading(true); setErro(null);
        try {
            const res = await fetch(`${API_BASE}/historico`, { headers });
            if (!res.ok) throw new Error('Erro ao buscar histórico.');
            setHistorico(await res.json());
            setPaginaHist(1);
        } catch (e) { setErro(e.message); } finally { setLoading(false); }
    }, [headers]);

    useEffect(() => {
        if (abaAtiva === 'historico') buscarHistorico();
        else if (abaAtiva === 'elegiveis' || abaAtiva === 'lancar') buscarElegiveis(); // compartilham a lista
        // 'desempenho' e 'modelos' têm dados próprios (não usam a lista de elegíveis)
    }, [abaAtiva, buscarElegiveis, buscarHistorico]);

    // ─── Derivados: Elegíveis (filtro → ordenação → paginação) ────────────────
    const aptos = useMemo(() => elegiveis.filter((e) => e.elegivel), [elegiveis]);
    // "Lançáveis" = elegíveis de hoje + os que estão AGUARDANDO reavaliação. O aguardando
    // entra para permitir o lançamento RETROATIVO (prova feita no papel, ex.: janela
    // Abril–Maio já fechada). Decisão Julia 24/09: confiar no operador; o backend valida
    // só a coerência da data de aplicação, não re-gateia pela janela.
    const lancaveis = useMemo(
        () => elegiveis.filter((e) => e.elegivel || e.status === 'AGUARDANDO_REAVALIACAO'),
        [elegiveis]
    );

    const elegiveisFiltrados = useMemo(() => elegiveis.filter((e) => {
        if (filtroCargo && e.CARGO_ABREV !== filtroCargo) return false;
        if (filtroLoja && e.CODIGO_LOCALIZACAO !== filtroLoja) return false;
        if (filtroStatus && e.status !== filtroStatus) return false;
        if (filtroNiveis.length && !filtroNiveis.includes(e.NIVEL)) return false;
        return true;
    }), [elegiveis, filtroCargo, filtroLoja, filtroStatus, filtroNiveis]);

    const elegiveisOrdenados = useMemo(
        () => aplicarSort(elegiveisFiltrados, sortElegiveis, NUMERICAS_ELEG),
        [elegiveisFiltrados, sortElegiveis]
    );
    const totalPaginas = Math.max(1, Math.ceil(elegiveisOrdenados.length / ITENS_POR_PAGINA));
    const elegiveisPagina = elegiveisOrdenados.slice((pagina - 1) * ITENS_POR_PAGINA, pagina * ITENS_POR_PAGINA);
    useEffect(() => { setPagina(1); }, [filtroCargo, filtroLoja, filtroStatus, filtroNiveis]);

    // Cargo é o filtro PRINCIPAL: nível e loja derivam do cargo escolhido.
    const porCargo = useMemo(
        () => (filtroCargo ? elegiveis.filter((e) => e.CARGO_ABREV === filtroCargo) : elegiveis),
        [elegiveis, filtroCargo]
    );
    // Níveis presentes no cargo escolhido (ex.: Coordenador → só JR/PL/SR), na ordem da escada.
    const niveisDisponiveis = useMemo(() => {
        const presentes = new Set(porCargo.map((e) => e.NIVEL).filter(Boolean));
        return ORDEM_NIVEIS.filter((n) => presentes.has(n));
    }, [porCargo]);
    const toggleNivel = (n) => setFiltroNiveis((prev) => prev.includes(n) ? prev.filter((x) => x !== n) : [...prev, n]);
    // Trocar o cargo (chip principal) zera os filtros dependentes.
    const escolherCargo = (c) => { setFiltroCargo(c); setFiltroNiveis([]); setFiltroLoja(''); setFiltroStatus(''); setPagina(1); };

    // ─── Derivados: Histórico ─────────────────────────────────────────────────
    const historicoFiltrado = useMemo(() => historico.filter((h) => {
        if (filtroCargoHist && h.CARGO_ABREV !== filtroCargoHist) return false;
        if (filtroResultadoHist && h.RESULTADO !== filtroResultadoHist) return false;
        return true;
    }), [historico, filtroCargoHist, filtroResultadoHist]);
    const historicoOrdenado = useMemo(
        () => aplicarSort(historicoFiltrado, sortHistorico, NUMERICAS_HIST),
        [historicoFiltrado, sortHistorico]
    );
    const totalPaginasHist = Math.max(1, Math.ceil(historicoOrdenado.length / ITENS_POR_PAGINA));
    const historicoPagina = historicoOrdenado.slice((paginaHist - 1) * ITENS_POR_PAGINA, paginaHist * ITENS_POR_PAGINA);
    useEffect(() => { setPaginaHist(1); }, [filtroCargoHist, filtroResultadoHist]);

    const handleSortElegiveis = (key) => { toggleSort(setSortElegiveis)(key); setPagina(1); };
    const handleSortHistorico = (key) => { toggleSort(setSortHistorico)(key); setPaginaHist(1); };

    // ─── Lançar avaliação ─────────────────────────────────────────────────────
    const selecionado = useMemo(
        () => lancaveis.find((e) => String(e.ID_FUNCIONARIO) === String(selecionadoId)) || null,
        [lancaveis, selecionadoId]
    );
    const previa = useMemo(() => calcularPrevia(notas.live, notas.teorica, notas.desempenho), [notas]);

    // Ao escolher o funcionário no lançamento, busca a última avaliação de Desempenho
    // FINALIZADA e pré-preenche a nota (o operador pode ajustar). Sem avaliação, deixa em branco.
    useEffect(() => {
        if (!selecionadoId) { setDesempenhoAuto(null); return; }
        let ativo = true;
        (async () => {
            try {
                const res = await fetch(`/api/rh/desempenho/avaliacao/funcionario/${selecionadoId}/ultima-finalizada`, { headers });
                if (!res.ok) return;
                const d = await res.json();
                if (!ativo) return;
                if (d && d.NOTA_FINAL != null) {
                    setDesempenhoAuto({ notaFinal: d.NOTA_FINAL });
                    setNotas((n) => ({ ...n, desempenho: String(d.NOTA_FINAL) }));
                } else {
                    setDesempenhoAuto(null);
                }
            } catch { /* sem avaliação de desempenho — segue com digitação manual */ }
        })();
        return () => { ativo = false; };
    }, [selecionadoId, headers]);

    const irParaLancar = (idFuncionario) => {
        setSelecionadoId(String(idFuncionario));
        setNotas({ live: '', teorica: '', desempenho: '' });
        setDataAplicacao(hojeISO());
        setObservacao('');
        setFeedback(null);
        setAbaAtiva('lancar');
    };

    const lancarAvaliacao = async () => {
        if (!selecionado || !previa) return;
        setSalvando(true); setFeedback(null);
        try {
            const res = await fetch(`${API_BASE}/avaliacao`, {
                method: 'POST', headers,
                body: JSON.stringify({
                    idFuncionario: selecionado.ID_FUNCIONARIO,
                    live: Number(notas.live), teorica: Number(notas.teorica), desempenho: Number(notas.desempenho),
                    dataAplicacao, observacao,
                }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Erro ao registrar avaliação.');
            setFeedback({ ok: true, msg: `${data.message} (final ${data.final})` });
            setSelecionadoId(''); setNotas({ live: '', teorica: '', desempenho: '' }); setDataAplicacao(hojeISO()); setObservacao('');
            buscarElegiveis(); // atualiza a lista (quem foi avaliado sai de "elegível")
        } catch (e) {
            setFeedback({ ok: false, msg: e.message });
        } finally { setSalvando(false); }
    };

    // ─── Estilos compartilhados ──────────────────────────────────────────────
    const thStyle = { padding: '12px 16px', textAlign: 'left', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', color: '#8b6464', borderBottom: '2px solid #f0e2e2', whiteSpace: 'nowrap' };
    const tdStyle = { padding: '14px 16px', fontSize: '14px', color: '#1e293b', borderBottom: '1px solid #f9f1f1', verticalAlign: 'middle' };
    const selectStyle = { padding: '7px 12px', borderRadius: '8px', border: '1px solid #f0e2e2', fontSize: '13px', color: '#1e293b', backgroundColor: '#fff', cursor: 'pointer', minWidth: '180px' };
    const inputNotaStyle = { width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #f0e2e2', fontSize: '15px', boxSizing: 'border-box' };
    const btnLimparStyle = { padding: '6px 12px', borderRadius: '8px', border: '1px solid #f0e2e2', fontSize: '12px', fontWeight: '600', color: '#8b6464', backgroundColor: '#fff', cursor: 'pointer' };

    const BadgeStatus = ({ status, label }) => {
        const est = ESTILO_STATUS[status] || ESTILO_STATUS.INDEFINIDO;
        const Icone = est.icon;
        return (
            <span title={label} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: '700', color: est.color, backgroundColor: est.bg }}>
                <Icone size={13} /> {label}
            </span>
        );
    };

    // Ícone de ordenação no cabeçalho (neutro / asc / desc).
    const SortIcon = ({ col, sortConfig }) => {
        if (!sortConfig || sortConfig.key !== col) return <ChevronsUpDown size={13} color="#cbd5e1" />;
        return sortConfig.dir === 'asc' ? <ChevronUp size={13} color="#0f172a" /> : <ChevronDown size={13} color="#0f172a" />;
    };
    // Cabeçalho clicável de ordenação.
    const ThSort = ({ col, children, sortConfig, onSort, center }) => (
        <th onClick={() => onSort(col)} style={{ ...thStyle, cursor: 'pointer', userSelect: 'none', textAlign: center ? 'center' : 'left' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', justifyContent: center ? 'center' : 'flex-start' }}>
                {children} <SortIcon col={col} sortConfig={sortConfig} />
            </span>
        </th>
    );

    // Chip clicável genérico. `primario` = destaque (cargo, vermelho cheio);
    // senão estilo secundário (nível/resultado, rosa claro).
    const labelFiltro = { fontSize: '13px', fontWeight: '700', color: '#8b6464', marginRight: '4px' };
    const Chip = ({ label, ativo, onClick, primario }) => {
        const at = primario ? { bg: '#d81d1d', fg: '#fff' } : { bg: '#fedbdb', fg: '#b91c1c' };
        return (
            <button onClick={onClick} title={ativo ? 'Clique para remover' : 'Clique para filtrar'}
                style={{
                    padding: primario ? '8px 18px' : '6px 14px', borderRadius: '999px', fontSize: '13px',
                    fontWeight: '700', cursor: 'pointer', transition: 'all 0.15s',
                    border: `1px solid ${ativo ? at.bg : '#e2cfcf'}`,
                    backgroundColor: ativo ? at.bg : '#fff', color: ativo ? at.fg : '#94a3b8',
                }}>
                {label}
            </button>
        );
    };

    // Card de status clicável: mostra a contagem e filtra ao clicar (toggle).
    const CardStatus = ({ chave, label, count, cor }) => {
        const ativo = chave === '' ? !filtroStatus : filtroStatus === chave;
        return (
            <button onClick={() => setFiltroStatus(filtroStatus === chave ? '' : chave)} title={chave === '' ? 'Mostrar todos' : `Filtrar: ${label}`}
                style={{
                    flex: '1 1 150px', minWidth: '140px', textAlign: 'left', cursor: 'pointer',
                    backgroundColor: '#fff', borderRadius: '12px', padding: '14px 16px',
                    border: `1px solid ${ativo ? cor : '#f0e2e2'}`, borderLeft: `4px solid ${cor}`,
                    boxShadow: ativo ? `0 0 0 1px ${cor}` : '0 1px 3px rgba(0,0,0,0.04)', transition: 'all 0.15s',
                }}>
                <div style={{ fontSize: '11px', fontWeight: '800', textTransform: 'uppercase', color: cor, marginBottom: '4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</div>
                <div style={{ fontSize: '24px', fontWeight: '800', color: '#0f172a' }}>{count}</div>
            </button>
        );
    };

    // Monitor: dias que o funcionário JÁ ESTÁ elegível sem ter a avaliação aplicada.
    // O contador só roda para quem está ELEGÍVEL (não-elegível não tem prazo ainda).
    // Verde até 30 dias; âmbar + alerta a partir de 30 (elegível e sem avaliação).
    const PrazoBadge = ({ row }) => {
        if (row.status !== 'ELEGIVEL') return <span style={{ color: '#cbd5e1' }}>—</span>;
        const ate = diasAte(row.dataElegivel);              // alvo − hoje (negativo = já passou)
        const decorridos = ate == null ? null : -ate;       // hoje − ficou elegível = dias elegível
        if (decorridos == null || decorridos < 0) return <span style={{ color: '#cbd5e1' }}>—</span>;
        const alerta = decorridos >= 30;
        const cor = alerta ? { c: '#9a3412', b: '#ffedd5' } : { c: '#065f46', b: '#d1fae5' };
        return (
            <span title={`Elegível desde ${formatarData(row.dataElegivel)} — ainda sem avaliação`}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: '700', color: cor.c, backgroundColor: cor.b }}>
                {alerta ? <AlertTriangle size={12} /> : <Clock size={12} />} há {decorridos}d
            </span>
        );
    };

    const Paginacao = ({ total, pagina, setPagina, totalPaginas }) => {
        if (totalPaginas <= 1) return null;
        const btn = { display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '7px 14px', borderRadius: '8px', border: '1px solid #f0e2e2', fontSize: '13px', fontWeight: '600', cursor: 'pointer', backgroundColor: '#fff', color: '#475569' };
        const btnOff = { ...btn, opacity: 0.4, cursor: 'not-allowed' };
        return (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', borderTop: '1px solid #f9f1f1' }}>
                <span style={{ fontSize: '13px', color: '#8b6464' }}>
                    {total} registro{total !== 1 ? 's' : ''}{totalPaginas > 1 && <> · página <strong>{pagina}</strong> de <strong>{totalPaginas}</strong></>}
                </span>
                <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={() => setPagina((p) => Math.max(1, p - 1))} disabled={pagina === 1} style={pagina === 1 ? btnOff : btn}><ChevronLeft size={15} /> Anterior</button>
                    <button onClick={() => setPagina((p) => Math.min(totalPaginas, p + 1))} disabled={pagina >= totalPaginas} style={pagina >= totalPaginas ? btnOff : btn}>Próxima <ChevronRight size={15} /></button>
                </div>
            </div>
        );
    };

    // ─── ABA: ELEGÍVEIS ──────────────────────────────────────────────────────
    // Base dos cards = elegíveis filtrados por cargo + loja + nível (SEM status),
    // para os cards mostrarem a contagem por status dentro da seleção atual.
    const baseCards = useMemo(() => elegiveis.filter((e) => {
        if (filtroCargo && e.CARGO_ABREV !== filtroCargo) return false;
        if (filtroLoja && e.CODIGO_LOCALIZACAO !== filtroLoja) return false;
        if (filtroNiveis.length && !filtroNiveis.includes(e.NIVEL)) return false;
        return true;
    }), [elegiveis, filtroCargo, filtroLoja, filtroNiveis]);
    const lojasDisponiveis = useMemo(() => [...new Set(porCargo.map((e) => e.CODIGO_LOCALIZACAO).filter(Boolean))].sort(), [porCargo]);
    const statusDisponiveis = useMemo(() => STATUS_ORDEM.filter((s) => elegiveis.some((e) => e.status === s)), [elegiveis]);
    const filtroElegAtivo = filtroCargo || filtroLoja || filtroStatus || filtroNiveis.length;

    const TabelaElegiveis = () => (
        <div>
            {/* Cargo — filtro PRINCIPAL (chips). Trocar zera nível/loja/situação. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
                <span style={labelFiltro}>Cargo:</span>
                <Chip label='Todos' ativo={!filtroCargo} onClick={() => escolherCargo('')} primario />
                <Chip label='Coordenador Q&S' ativo={filtroCargo === 'COORD. QUAL E SERV'} onClick={() => escolherCargo('COORD. QUAL E SERV')} primario />
                <Chip label='Gerente de Plantão' ativo={filtroCargo === 'GERENTE PLANTAO'} onClick={() => escolherCargo('GERENTE PLANTAO')} primario />
            </div>

            {/* Situação — cards (contam sobre cargo+loja+nível; filtram ao clicar) */}
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
                <CardStatus chave='' label='Total' count={baseCards.length} cor='#475569' />
                {STATUS_ORDEM.filter((s) => baseCards.some((e) => e.status === s)).map((s) => (
                    <CardStatus key={s} chave={s} label={ROTULO_STATUS[s] || s}
                        count={baseCards.filter((e) => e.status === s).length}
                        cor={(ESTILO_STATUS[s] || ESTILO_STATUS.INDEFINIDO).color} />
                ))}
            </div>

            {/* Nível (chips, só os do cargo) · Loja · Limpar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
                {niveisDisponiveis.length > 0 && (
                    <>
                        <span style={labelFiltro}>Nível:</span>
                        {niveisDisponiveis.map((n) => (
                            <Chip key={n} label={n} ativo={filtroNiveis.includes(n)} onClick={() => toggleNivel(n)} />
                        ))}
                    </>
                )}
                <span style={{ ...labelFiltro, marginLeft: niveisDisponiveis.length ? '8px' : 0 }}>Loja:</span>
                <select value={filtroLoja} onChange={(e) => { setFiltroLoja(e.target.value); setPagina(1); }} style={selectStyle}>
                    <option value=''>Todas as lojas</option>
                    {lojasDisponiveis.map((l) => (
                        <option key={l} value={l}>{l}</option>
                    ))}
                </select>
                <select value={filtroStatus} onChange={(e) => setFiltroStatus(e.target.value)} style={selectStyle}>
                    <option value=''>Todas as situações</option>
                    {statusDisponiveis.map((s) => (
                        <option key={s} value={s}>{ROTULO_STATUS[s] || s}</option>
                    ))}
                </select>
                {filtroElegAtivo ? (
                    <button onClick={() => { setFiltroCargo(''); setFiltroLoja(''); setFiltroStatus(''); setFiltroNiveis([]); }} style={btnLimparStyle}>✕ Limpar filtros</button>
                ) : null}
            </div>

            {elegiveisOrdenados.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px 20px', color: '#8b6464' }}>
                    <TrendingUp size={48} color="#b89494" style={{ marginBottom: '16px' }} />
                    <p style={{ fontSize: '16px', fontWeight: '600', margin: 0 }}>Nenhum funcionário para os filtros selecionados.</p>
                </div>
            ) : (
                <div style={{ border: '1px solid #f0e2e2', borderRadius: '10px', overflow: 'hidden' }}>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead><tr style={{ backgroundColor: '#fcf8f8' }}>
                                <ThSort col="CODIGO_LOCALIZACAO" sortConfig={sortElegiveis} onSort={handleSortElegiveis}>Loja</ThSort>
                                <ThSort col="NOME_PESSOA" sortConfig={sortElegiveis} onSort={handleSortElegiveis}>Nome</ThSort>
                                <ThSort col="CARGO_ABREV" sortConfig={sortElegiveis} onSort={handleSortElegiveis}>Cargo</ThSort>
                                <ThSort col="NIVEL" sortConfig={sortElegiveis} onSort={handleSortElegiveis}>Nível</ThSort>
                                <ThSort col="REGIME" sortConfig={sortElegiveis} onSort={handleSortElegiveis}>Regime</ThSort>
                                <ThSort col="DATA_INICIO_CARGO" sortConfig={sortElegiveis} onSort={handleSortElegiveis}>Início no cargo</ThSort>
                                <ThSort col="status" sortConfig={sortElegiveis} onSort={handleSortElegiveis}>Situação</ThSort>
                                <ThSort col="QTD_REPROVACOES" sortConfig={sortElegiveis} onSort={handleSortElegiveis} center>Reprov.</ThSort>
                                <ThSort col="ULTIMA_DATA" sortConfig={sortElegiveis} onSort={handleSortElegiveis}>Última aval.</ThSort>
                                <ThSort col="dataElegivel" sortConfig={sortElegiveis} onSort={handleSortElegiveis}>Dias elegível</ThSort>
                                <th style={{ ...thStyle, textAlign: 'center' }}>Ação</th>
                            </tr></thead>
                            <tbody>
                                {elegiveisPagina.map((row) => (
                                    <tr key={row.ID_FUNCIONARIO}
                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fcf8f8'}
                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                                        <td style={tdStyle}>{row.CODIGO_LOCALIZACAO || '-'}</td>
                                        <td style={{ ...tdStyle, fontWeight: '600' }}>{row.NOME_PESSOA || '-'}</td>
                                        <td style={tdStyle}>{row.CARGO_ABREV === 'COORD. QUAL E SERV' ? 'Coord. Q&S' : 'Ger. Plantão'}</td>
                                        <td style={{ ...tdStyle, fontWeight: '600' }}>{row.NIVEL || '-'}</td>
                                        <td style={tdStyle}>{row.REGIME === 'JUNIOR' ? '6 meses' : 'Anual'}</td>
                                        <td style={tdStyle}>{formatarData(row.DATA_INICIO_CARGO)}</td>
                                        <td style={tdStyle}><BadgeStatus status={row.status} label={row.label} /></td>
                                        <td style={{ ...tdStyle, textAlign: 'center', fontWeight: row.QTD_REPROVACOES ? '700' : '400', color: row.QTD_REPROVACOES ? '#b91c1c' : '#cbd5e1' }}>{row.QTD_REPROVACOES}</td>
                                        <td style={tdStyle}>{row.ULTIMA_DATA ? formatarData(row.ULTIMA_DATA) : '—'}</td>
                                        <td style={tdStyle}><PrazoBadge row={row} /></td>
                                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                                            {(() => {
                                                // Elegível → lançamento normal (vermelho cheio).
                                                // Aguardando reavaliação → lançamento RETROATIVO (âmbar,
                                                // rótulo próprio): prova feita no papel ainda não registrada.
                                                const retroativo = !row.elegivel && row.status === 'AGUARDANDO_REAVALIACAO';
                                                if (!row.elegivel && !retroativo) return <span style={{ color: '#cbd5e1' }}>—</span>;
                                                return (
                                                    <button onClick={() => irParaLancar(row.ID_FUNCIONARIO)}
                                                        title={retroativo ? 'Lançar avaliação retroativa (prova já aplicada)' : 'Lançar avaliação'}
                                                        style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '7px 14px', borderRadius: '8px', border: 'none', backgroundColor: retroativo ? '#c2680c' : '#d81d1d', color: '#fff', fontSize: '13px', fontWeight: '700', cursor: 'pointer' }}>
                                                        <ClipboardPenLine size={14} /> {retroativo ? 'Retroativo' : 'Lançar'}
                                                    </button>
                                                );
                                            })()}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <Paginacao total={elegiveisOrdenados.length} pagina={pagina} setPagina={setPagina} totalPaginas={totalPaginas} />
                </div>
            )}
        </div>
    );

    // ─── ABA: LANÇAR AVALIAÇÃO ───────────────────────────────────────────────
    const campoNota = (label, chave) => (
        <div style={{ flex: 1, minWidth: '140px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#694747', marginBottom: '6px' }}>{label} <span style={{ fontWeight: '400', color: '#b89494' }}>(0–100)</span></label>
            <input type="number" min="0" max="100" step="0.01" value={notas[chave]}
                onChange={(e) => setNotas((n) => ({ ...n, [chave]: e.target.value }))}
                style={inputNotaStyle} placeholder="0,00" />
        </div>
    );

    const FormLancar = () => (
        <div style={{ maxWidth: '720px' }}>
            <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#694747', marginBottom: '6px' }}>Funcionário</label>
                <select value={selecionadoId} onChange={(e) => { setSelecionadoId(e.target.value); setFeedback(null); }} style={{ ...selectStyle, width: '100%', minWidth: 0 }}>
                    <option value=''>Selecione… ({aptos.length} elegíveis{lancaveis.length > aptos.length ? ` + ${lancaveis.length - aptos.length} retroativo${lancaveis.length - aptos.length !== 1 ? 's' : ''}` : ''})</option>
                    {lancaveis.map((a) => (
                        <option key={a.ID_FUNCIONARIO} value={a.ID_FUNCIONARIO}>
                            {a.NOME_PESSOA} — {a.NIVEL} → {a.PROXIMO_NIVEL || '?'} ({a.CARGO_ABREV === 'COORD. QUAL E SERV' ? 'Coord. Q&S' : 'Ger. Plantão'}){a.elegivel ? '' : ' · retroativo'}
                        </option>
                    ))}
                </select>
            </div>

            {selecionado && (
                <div style={{ backgroundColor: '#fcf8f8', borderRadius: '10px', padding: '16px', marginBottom: '20px', fontSize: '13px', color: '#694747' }}>
                    <strong>{selecionado.NOME_PESSOA}</strong> · {selecionado.NIVEL} → <strong>{selecionado.PROXIMO_NIVEL || '?'}</strong> · regime {selecionado.REGIME === 'JUNIOR' ? '6 meses' : 'anual'} · início no cargo {formatarData(selecionado.DATA_INICIO_CARGO)} · reprovações <strong>{selecionado.QTD_REPROVACOES}</strong> · última aval. {selecionado.ULTIMA_DATA ? formatarData(selecionado.ULTIMA_DATA) : '—'}
                </div>
            )}

            {/* Data de aplicação — hoje por padrão (lançamento do dia é um clique);
                uma data anterior registra avaliações já realizadas no papel. O
                backend usa esta data para a janela de reavaliação seguinte. */}
            <div style={{ marginBottom: '20px', maxWidth: '260px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#694747', marginBottom: '6px' }}>Data de aplicação</label>
                <input type="date" value={dataAplicacao} max={hojeISO()}
                    onChange={(e) => setDataAplicacao(e.target.value)}
                    style={inputNotaStyle} />
                <div style={{ fontSize: '12px', color: '#b89494', marginTop: '6px' }}>
                    Quando a avaliação foi aplicada. Deixe hoje para lançamentos do dia; use uma data anterior para registrar avaliações já realizadas.
                </div>
            </div>

            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: desempenhoAuto ? '8px' : '20px' }}>
                {campoNota('LIVE (prática)', 'live')}
                {campoNota('Teórica', 'teorica')}
                {campoNota('Desempenho', 'desempenho')}
            </div>
            {desempenhoAuto && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', marginBottom: '20px', borderRadius: '8px', backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af', fontSize: '12px' }}>
                    <Info size={14} /> Nota de Desempenho <strong>{desempenhoAuto.notaFinal}</strong> pré-preenchida da última avaliação de Desempenho finalizada — ajuste se necessário.
                </div>
            )}

            {/* Prévia do cálculo (informativa — o backend recalcula ao salvar) */}
            {previa && (
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center', padding: '16px', borderRadius: '10px', marginBottom: '20px', backgroundColor: previa.resultado === 'APROVADO' ? '#d1fae5' : '#fee2e2', border: `1px solid ${previa.resultado === 'APROVADO' ? '#6ee7b7' : '#fecaca'}` }}>
                    <span style={{ fontSize: '13px', color: '#475569' }}>Operacional <strong>{previa.operacional}</strong> (informativa)</span>
                    <span style={{ fontSize: '15px', color: '#0f172a' }}>Nota final <strong style={{ fontSize: '18px' }}>{previa.final}</strong></span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontWeight: '800', color: previa.resultado === 'APROVADO' ? '#065f46' : '#b91c1c' }}>
                        {previa.resultado === 'APROVADO' ? <CheckCircle size={18} /> : <XCircle size={18} />} {previa.resultado}
                    </span>
                    <span style={{ fontSize: '12px', color: '#8b6464' }}>(prévia — corte ≥ 90)</span>
                </div>
            )}

            <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#694747', marginBottom: '6px' }}>Observação <span style={{ fontWeight: '400', color: '#b89494' }}>(opcional)</span></label>
                <textarea value={observacao} onChange={(e) => setObservacao(e.target.value)} rows={2} style={{ ...inputNotaStyle, resize: 'vertical', fontFamily: 'inherit' }} />
            </div>

            {feedback && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', borderRadius: '10px', marginBottom: '16px', backgroundColor: feedback.ok ? '#d1fae5' : '#fee2e2', color: feedback.ok ? '#065f46' : '#b91c1c', fontWeight: '600', fontSize: '14px' }}>
                    {feedback.ok ? <CheckCircle size={18} /> : <AlertTriangle size={18} />} {feedback.msg}
                </div>
            )}

            <button onClick={lancarAvaliacao} disabled={!selecionado || !previa || salvando}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '12px 24px', borderRadius: '8px', border: 'none', backgroundColor: (!selecionado || !previa || salvando) ? '#fd9393' : '#d81d1d', color: '#fff', fontSize: '15px', fontWeight: '700', cursor: (!selecionado || !previa || salvando) ? 'not-allowed' : 'pointer' }}>
                <CheckCircle size={17} /> {salvando ? 'Registrando…' : 'Registrar avaliação'}
            </button>
        </div>
    );

    // ─── ABA: HISTÓRICO ──────────────────────────────────────────────────────
    const filtroHistAtivo = filtroCargoHist || filtroResultadoHist;

    const TabelaHistorico = () => (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
                <span style={labelFiltro}>Cargo:</span>
                <Chip label='Todos' ativo={!filtroCargoHist} onClick={() => setFiltroCargoHist('')} primario />
                <Chip label='Coordenador Q&S' ativo={filtroCargoHist === 'COORD. QUAL E SERV'} onClick={() => setFiltroCargoHist('COORD. QUAL E SERV')} primario />
                <Chip label='Gerente de Plantão' ativo={filtroCargoHist === 'GERENTE PLANTAO'} onClick={() => setFiltroCargoHist('GERENTE PLANTAO')} primario />
                <span style={{ ...labelFiltro, marginLeft: '8px' }}>Resultado:</span>
                <Chip label='Todos' ativo={!filtroResultadoHist} onClick={() => setFiltroResultadoHist('')} />
                <Chip label='Aprovado' ativo={filtroResultadoHist === 'APROVADO'} onClick={() => setFiltroResultadoHist('APROVADO')} />
                <Chip label='Reprovado' ativo={filtroResultadoHist === 'REPROVADO'} onClick={() => setFiltroResultadoHist('REPROVADO')} />
                {filtroHistAtivo ? (
                    <button onClick={() => { setFiltroCargoHist(''); setFiltroResultadoHist(''); }} style={btnLimparStyle}>✕ Limpar filtros</button>
                ) : null}
            </div>

            {historicoOrdenado.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px 20px', color: '#8b6464' }}>
                    <History size={48} color="#b89494" style={{ marginBottom: '16px' }} />
                    <p style={{ fontSize: '16px', fontWeight: '600', margin: 0 }}>Nenhuma avaliação registrada {historico.length > 0 ? 'para os filtros' : 'ainda'}.</p>
                </div>
            ) : (
                <div style={{ border: '1px solid #f0e2e2', borderRadius: '10px', overflow: 'hidden' }}>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead><tr style={{ backgroundColor: '#fcf8f8' }}>
                                <ThSort col="CODIGO_LOCALIZACAO" sortConfig={sortHistorico} onSort={handleSortHistorico}>Loja</ThSort>
                                <ThSort col="DATA_AVALIACAO" sortConfig={sortHistorico} onSort={handleSortHistorico}>Data</ThSort>
                                <ThSort col="NOME_FUNCIONARIO" sortConfig={sortHistorico} onSort={handleSortHistorico}>Nome</ThSort>
                                <ThSort col="NIVEL_ORIGEM" sortConfig={sortHistorico} onSort={handleSortHistorico}>Transição</ThSort>
                                <ThSort col="NOTA_LIVE" sortConfig={sortHistorico} onSort={handleSortHistorico} center>LIVE</ThSort>
                                <ThSort col="NOTA_TEORICA" sortConfig={sortHistorico} onSort={handleSortHistorico} center>Teórica</ThSort>
                                <ThSort col="NOTA_DESEMPENHO" sortConfig={sortHistorico} onSort={handleSortHistorico} center>Desemp.</ThSort>
                                <ThSort col="NOTA_OPERACIONAL" sortConfig={sortHistorico} onSort={handleSortHistorico} center>Operac.</ThSort>
                                <ThSort col="NOTA_FINAL" sortConfig={sortHistorico} onSort={handleSortHistorico} center>Final</ThSort>
                                <ThSort col="RESULTADO" sortConfig={sortHistorico} onSort={handleSortHistorico} center>Resultado</ThSort>
                                <ThSort col="EFETIVADA" sortConfig={sortHistorico} onSort={handleSortHistorico} center>Efetivada</ThSort>
                            </tr></thead>
                            <tbody>
                                {historicoPagina.map((row) => (
                                    <tr key={row.ID}
                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fcf8f8'}
                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                                        <td style={tdStyle}>{row.CODIGO_LOCALIZACAO || '-'}</td>
                                        <td style={tdStyle}>{formatarData(row.DATA_AVALIACAO)}</td>
                                        <td style={{ ...tdStyle, fontWeight: '600' }}>{row.NOME_FUNCIONARIO || '-'}</td>
                                        <td style={tdStyle}>{row.NIVEL_ORIGEM} → {row.NOME_CARGO_DESTINO ? row.NOME_CARGO_DESTINO.replace(row.CARGO_ABREV, '').trim() : '?'}</td>
                                        <td style={{ ...tdStyle, textAlign: 'center' }}>{row.NOTA_LIVE}</td>
                                        <td style={{ ...tdStyle, textAlign: 'center' }}>{row.NOTA_TEORICA}</td>
                                        <td style={{ ...tdStyle, textAlign: 'center' }}>{row.NOTA_DESEMPENHO}</td>
                                        <td style={{ ...tdStyle, textAlign: 'center', color: '#64748b' }}>{row.NOTA_OPERACIONAL}</td>
                                        <td style={{ ...tdStyle, textAlign: 'center', fontWeight: '700' }}>{row.NOTA_FINAL}</td>
                                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 9px', borderRadius: '999px', fontSize: '12px', fontWeight: '700', color: row.RESULTADO === 'APROVADO' ? '#065f46' : '#b91c1c', backgroundColor: row.RESULTADO === 'APROVADO' ? '#d1fae5' : '#fee2e2' }}>
                                                {row.RESULTADO === 'APROVADO' ? <CheckCircle size={12} /> : <XCircle size={12} />} {row.RESULTADO}
                                            </span>
                                        </td>
                                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                                            {row.RESULTADO === 'APROVADO'
                                                ? (row.EFETIVADA
                                                    ? <span style={{ color: '#065f46', fontWeight: '700', display: 'inline-flex', alignItems: 'center', gap: '4px' }}><ShieldCheck size={14} /> Sim</span>
                                                    : <span style={{ color: '#92400e', fontWeight: '700', display: 'inline-flex', alignItems: 'center', gap: '4px' }}><Hourglass size={14} /> Pendente</span>)
                                                : <span style={{ color: '#cbd5e1' }}>—</span>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <Paginacao total={historicoOrdenado.length} pagina={paginaHist} setPagina={setPaginaHist} totalPaginas={totalPaginasHist} />
                </div>
            )}
        </div>
    );

    // ─── Render principal ────────────────────────────────────────────────────
    const ABAS = [
        { id: 'elegiveis', label: 'Elegíveis', icon: ListChecks, badge: aptos.length },
        { id: 'lancar', label: 'Lançar Avaliação', icon: ClipboardPenLine, badge: null },
        { id: 'desempenho', label: 'Desempenho', icon: ClipboardCheck, badge: null },
        { id: 'modelos', label: 'Modelos', icon: LayoutTemplate, badge: null },
        { id: 'historico', label: 'Histórico', icon: History, badge: null },
    ];

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
                <div>
                    <h3 style={{ margin: '0 0 4px 0', fontSize: '20px', fontWeight: '800', color: '#0f172a' }}>Transição de Cargo</h3>
                    <p style={{ margin: 0, fontSize: '14px', color: '#8b6464' }}>
                        Promoções de Coordenador Q&S e Gerente de Plantão — elegibilidade, lançamento de notas e histórico.
                    </p>
                </div>
                <button onClick={() => abaAtiva === 'historico' ? buscarHistorico() : buscarElegiveis()} disabled={loading}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 16px', borderRadius: '8px', border: '1px solid #f0e2e2', backgroundColor: '#fff', color: '#694747', fontSize: '14px', fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer' }}>
                    <RefreshCw size={15} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} /> Atualizar
                </button>
            </div>

            <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', backgroundColor: '#f9f1f1', padding: '4px', borderRadius: '10px', width: 'fit-content', flexWrap: 'wrap' }}>
                {ABAS.map(({ id, label, icon: Icone, badge }) => (
                    <button key={id} onClick={() => setAbaAtiva(id)}
                        style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '8px 18px', borderRadius: '7px', border: 'none', fontWeight: '700', fontSize: '14px', cursor: 'pointer', backgroundColor: abaAtiva === id ? '#fff' : 'transparent', color: abaAtiva === id ? '#d81d1d' : '#8b6464', boxShadow: abaAtiva === id ? '0 1px 4px rgba(0,0,0,0.08)' : 'none' }}>
                        <Icone size={15} /> {label}
                        {badge !== null && badge > 0 && (
                            <span style={{ backgroundColor: '#d81d1d', color: '#fff', borderRadius: '999px', fontSize: '11px', fontWeight: '800', padding: '1px 7px', minWidth: '20px', textAlign: 'center' }}>{badge}</span>
                        )}
                    </button>
                ))}
            </div>

            {erro && (
                <div style={{ padding: '14px 18px', backgroundColor: '#fee2e2', borderRadius: '10px', color: '#dc2626', fontWeight: '600', marginBottom: '16px' }}>{erro}</div>
            )}

            {/* Render por chamada de função (não <Componente/>): evita remontar a
                cada tecla — preserva o foco nos inputs da aba Lançar. */}
            {loading && (abaAtiva === 'elegiveis' || abaAtiva === 'historico') ? (
                <div style={{ textAlign: 'center', padding: '60px', color: '#8b6464' }}><p style={{ fontSize: '15px' }}>Carregando…</p></div>
            ) : (
                abaAtiva === 'elegiveis' ? TabelaElegiveis()
                    : abaAtiva === 'lancar' ? FormLancar()
                        : abaAtiva === 'desempenho' ? <RHDesempenhoAvaliacao />
                            : abaAtiva === 'modelos' ? <RHDesempenhoVersoes />
                                : TabelaHistorico()
            )}
        </div>
    );
};

export default RHTransicaoCargo;
